import React, { useState, useEffect, useRef } from "react";
import { Grid, Modal } from "@mui/material";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import KendoButton from "assets/theme/components/button/kendo";
import { useFormik } from "formik";
import * as Yup from "yup";
import { OrganizationService } from "api/organizations.api";
import toast from "react-hot-toast";
import { modalContainerStyle } from "pages/components/common/Styles";

const OrganizationListModal = ({ organizationListOpen, handleOrganizationListClose }) => {
  const [loading, setLoading] = React.useState(false);
  const [data, setData] = useState(null);
  let initialValues = {
    organization: "",
  };

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: Yup.object({
      organization: Yup.string().required("This field is required"),
    }),
    onSubmit: (values) => {
      const selectedOrg = data?.find(org => org.subDomain === values.organization);
      if (selectedOrg) {
        // Set subdomain cookie for cross-domain session transfer (secure, same-site)
        const cookieOptions = 'path=/; secure; samesite=strict; max-age=3600';
        document.cookie = `_subdomain=${selectedOrg.subDomain}; ${cookieOptions}`;

        // Redirect without token in URL - token stays in localStorage
        // The target domain will read the cookie and validate the existing token
        window.location.replace(selectedOrg.url);
      }
    },
  });

  useEffect(() => {
    const controller = new AbortController();
    const getUserOrganizations = async () => {
      try {
        setLoading(true);
        const response = await OrganizationService.getOrganizationsList(controller.signal);
        setData(response?.data?.data);
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error(error);
          if (!!error?.response?.data?.message) {
            toast.error(error?.response?.data?.message);
          } else {
            toast.error(error?.message || "Something went wrong");
          }
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };
    getUserOrganizations();
    return () => controller.abort();
  }, [organizationListOpen]);

  return (
    <Modal
      open={organizationListOpen}
      onClose={handleOrganizationListClose}
      aria-labelledby="invite-user-modal"
      aria-describedby="invite-user-modal-description"
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <SoftBox sx={modalContainerStyle}>
        <SoftTypography variant="h4" component="h2" gutterBottom mb={3}>
          Switch Organization
        </SoftTypography>
        <SoftBox component="form" role="form" onSubmit={formik.handleSubmit}>
          <SoftBox mb={2}>
            <select
              name="organization"
              className="custom-select"
              value={formik.values.organization}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            >
              <option value="">Select Organization</option>
              {data &&
                data?.length > 0 &&
                data.map((item) => {
                  return <option key={item.subDomain} value={item.subDomain}>{item.name}</option>;
                })}
            </select>
            {formik.touched.organization && formik.errors.organization ? (
              <div className="form-error error">{formik.errors.organization}</div>
            ) : null}
          </SoftBox>
          <Grid container justifyContent="flex-end" mt={3}>
            <Grid item lg={4}>
              <SoftButton onClick={handleOrganizationListClose} color="dark" sx={{ mr: 1 }}>
                Cancel
              </SoftButton>
            </Grid>
            <Grid item lg={4}>
              <KendoButton variant="contained" autoFocus label="Switch" type="submit" />
            </Grid>
          </Grid>
        </SoftBox>
      </SoftBox>
    </Modal>
  );
};

export default OrganizationListModal;
